import { useState } from "react";
import { Link, useOutletContext } from "react-router";
import { useFindings } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { number } from "./imports-helpers.js";
import {
  SEVERITY_COLOUR,
  slaText,
  STATUS_LABEL,
  statusTone,
  TABS,
  type FindingTab,
} from "./findings-helpers.js";

/**
 * Data Assurance · Findings (FND-06, AC-2-06), as the prototype draws it:
 * the four views as tabs, the table with the severity stripe, the rule and
 * reference with the explanation under it, the SPID, the owner and party,
 * the status pill, the SLA (late in red) and the impact, sorted by impact.
 */
export function Findings() {
  const { client } = useOutletContext<ShellContext>();
  const [tab, setTab] = useState<FindingTab>("all");
  const view = TABS.find((t) => t.key === tab)!.view;
  const findings = useFindings(client, { view, limit: 100, sort: "impact_desc" });
  const items = findings.data?.items ?? [];

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Data Assurance · Work queue</div>
          <h1>Findings</h1>
        </div>
        <div className="tabs" aria-label="Views">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-pressed={tab === t.key}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <section className="card">
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="sr-only">Severity</span>
                </th>
                <th>Finding</th>
                <th>SPID</th>
                <th>Owner</th>
                <th>Status</th>
                <th>SLA</th>
                <th className="num">Impact</th>
              </tr>
            </thead>
            <tbody>
              {findings.isPending ? (
                [0, 1, 2].map((i) => (
                  <tr key={i}>
                    <td colSpan={7}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))
              ) : findings.isError ? (
                <tr>
                  <td colSpan={7} className="empty">
                    The findings could not be loaded: {findings.error.message}. Try again in a
                    moment.
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty">
                    Nothing in this view.
                  </td>
                </tr>
              ) : (
                items.map((f) => {
                  const sla = slaText(f);
                  return (
                    <tr key={f.id} className="click">
                      <td className="sev">
                        <i
                          style={{ background: SEVERITY_COLOUR[f.severity] }}
                          aria-label={`Severity ${f.severity}`}
                          role="img"
                        />
                      </td>
                      <td>
                        <Link to={`/findings/${f.id}`} className="finding-link">
                          <b>{f.ruleName}</b>{" "}
                          <span className="mono faint" style={{ fontSize: ".7rem" }}>
                            {f.ref} · {f.ruleId}
                          </span>
                        </Link>
                        <br />
                        <span className="muted" style={{ fontSize: ".78rem" }}>
                          {f.explanation}
                        </span>
                      </td>
                      <td className="mono" style={{ fontSize: ".78rem" }}>
                        {f.spid ?? "none"}
                      </td>
                      <td>
                        {f.ownerTeamName ?? f.ownerPartyName}
                        <br />
                        <span className="faint" style={{ fontSize: ".74rem" }}>
                          {f.ownerTeamName ? f.ownerPartyName : "Retailer"}
                        </span>
                      </td>
                      <td>
                        <span className={`pill ${statusTone(f.status)}`}>
                          {STATUS_LABEL[f.status]}
                        </span>
                      </td>
                      <td>
                        <span
                          className="mono"
                          style={
                            sla.tone === "late"
                              ? { color: "var(--crit)", fontWeight: 600 }
                              : sla.tone === "soon"
                                ? { color: "var(--warn)" }
                                : { color: "var(--muted)" }
                          }
                        >
                          {sla.label}
                        </span>
                      </td>
                      <td className="num">{f.impactScore.toFixed(1)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      {findings.data ? (
        <p className="faint" style={{ fontSize: ".78rem", margin: 0 }}>
          Showing {number(items.length)} of {number(findings.data.total)} in this view.
        </p>
      ) : null}
    </>
  );
}
